import { ActionIcon, Box, Button, Select, Table, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { quantityInWords } from '@erp/shared';
import type { UnitOfMeasure } from '../api/catalogs';
import type { Nomenclature } from '../api/nomenclature';

export interface PoaLineRow {
  key: string;
  nomenclatureId: string | null;
  name: string;
  unitId: string;
  quantity: string;
}

export function emptyPoaLineRow(): PoaLineRow {
  return {
    key: crypto.randomUUID(),
    nomenclatureId: null,
    name: '',
    unitId: '',
    quantity: '1',
  };
}

interface Props {
  lines: PoaLineRow[];
  onChange: (lines: PoaLineRow[]) => void;
  canEdit: boolean;
  nomenclatureOptions: Nomenclature[];
  units: UnitOfMeasure[];
}

/**
 * Оборотная сторона М-2: перечень ТМЦ без цен и сумм — доверенность даёт право
 * получить количество, а не оплатить стоимость. Количество в бланке печатается
 * прописью, поэтому показываем его тут же: так видно, что попадёт на бумагу.
 */
export function PowerOfAttorneyLinesEditor({ lines, onChange, canEdit, nomenclatureOptions, units }: Props) {
  const nomenclatureSelectOptions = nomenclatureOptions.map((n) => ({ value: n.id, label: n.name }));
  const unitOptions = units.map((u) => ({ value: u.id, label: u.symbol }));

  function updateRow(key: string, patch: Partial<PoaLineRow>) {
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function pickNomenclature(key: string, nomenclatureId: string | null) {
    const item = nomenclatureOptions.find((n) => n.id === nomenclatureId);
    if (!item) {
      updateRow(key, { nomenclatureId: null });
      return;
    }
    updateRow(key, { nomenclatureId: item.id, name: item.name, unitId: item.unitId });
  }

  function quantityWords(raw: string): string {
    const normalized = raw.trim().replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(normalized)) return '';
    return quantityInWords(normalized);
  }

  return (
    <Box>
      <Box style={{ overflowX: 'auto' }}>
        <Table verticalSpacing={6} miw={860}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>№</Table.Th>
              <Table.Th w={220}>Из номенклатуры</Table.Th>
              <Table.Th>Наименование ценностей</Table.Th>
              <Table.Th w={110}>Ед.</Table.Th>
              <Table.Th w={100}>Кол-во</Table.Th>
              <Table.Th w={200}>Прописью</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((row, index) => (
              <Table.Tr key={row.key}>
                <Table.Td>{index + 1}</Table.Td>
                <Table.Td>
                  <Select
                    size="xs"
                    placeholder="Выбрать"
                    data={nomenclatureSelectOptions}
                    value={row.nomenclatureId}
                    onChange={(v) => pickNomenclature(row.key, v)}
                    disabled={!canEdit}
                    searchable
                    clearable
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    value={row.name}
                    onChange={(e) => updateRow(row.key, { name: e.currentTarget.value })}
                    disabled={!canEdit}
                    required
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    size="xs"
                    data={unitOptions}
                    value={row.unitId || null}
                    onChange={(v) => updateRow(row.key, { unitId: v ?? '' })}
                    disabled={!canEdit}
                    searchable
                    clearable
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    value={row.quantity}
                    onChange={(e) => updateRow(row.key, { quantity: e.currentTarget.value })}
                    disabled={!canEdit}
                  />
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {quantityWords(row.quantity) || '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {canEdit && (
                    <ActionIcon variant="subtle" color="red" onClick={() => onChange(lines.filter((l) => l.key !== row.key))}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>

      {canEdit && (
        <Button
          mt="xs"
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={() => onChange([...lines, emptyPoaLineRow()])}
        >
          Добавить строку
        </Button>
      )}
    </Box>
  );
}
